
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { cozeApiService } from '../../services/cozeApi';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  data?: any; // 用于存储结构化数据
}

export default function SaleCalendar() {
  const [searchParams] = useSearchParams();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [displayData, setDisplayData] = useState<any>(null);
  const [displayType, setDisplayType] = useState<'report' | 'summary' | 'json'>('report');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // 从URL参数获取工具信息
  const toolName = searchParams.get('toolName') || '销售日历助手';
  const functionName = searchParams.get('functionName') || '';
  const userRole = searchParams.get('role') || '';
  const userName = searchParams.get('user') || '';

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    // 添加欢迎消息
    const welcomeMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'assistant',
      content: '您好！我是销售日历助手，我可以帮您：\n• 管理销售日程和客户拜访\n• 制定销售计划和目标\n• 分析销售数据和趋势\n• 生成销售报告和总结\n\n请告诉我您需要什么销售日历功能？',
      timestamp: Date.now()
    };
    setMessages([welcomeMessage]);
  }, [navigate]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const generateId = () => Date.now().toString() + Math.random().toString(36).substr(2, 9);

  // 解析智能体回复，提取结构化数据
  const parseAssistantResponse = (response: string) => {
    try {
      // 尝试解析JSON格式的回复
      if (response.includes('{') && response.includes('}')) {
        const jsonMatch = response.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const jsonData = JSON.parse(jsonMatch[0]);
          return {
            type: 'json',
            title: '智能体数据回复',
            data: jsonData,
            originalText: response
          };
        }
      }

      // 根据回复内容智能判断类型
      const lowerResponse = response.toLowerCase();
      
      if (lowerResponse.includes('日程') || lowerResponse.includes('安排') || lowerResponse.includes('schedule')) {
        return {
          type: 'report',
          title: '销售日程安排',
          data: {
            period: '基于智能体分析',
            summary: response,
            originalResponse: response
          },
          originalText: response
        };
      } else if (lowerResponse.includes('客户') || lowerResponse.includes('拜访') || lowerResponse.includes('client')) {
        return {
          type: 'report',
          title: '客户拜访计划',
          data: {
            period: '基于智能体分析',
            summary: response,
            originalResponse: response
          },
          originalText: response
        };
      } else if (lowerResponse.includes('销售') || lowerResponse.includes('业绩') || lowerResponse.includes('sales')) {
        return {
          type: 'summary',
          title: '销售分析报告',
          data: {
            projectName: '基于智能体分析的销售数据',
            analysis: response,
            originalResponse: response
          },
          originalText: response
        };
      } else {
        // 默认作为销售报告处理
        return {
          type: 'report',
          title: '销售日历',
          data: {
            period: '智能体生成',
            summary: response,
            originalResponse: response
          },
          originalText: response
        };
      }
    } catch (error) {
      console.error('解析智能体回复时出错:', error);
      // 如果解析失败，返回原始文本
      return {
        type: 'report',
        title: '智能体回复',
        data: {
          period: '原始回复',
          summary: response,
          originalResponse: response
        },
        originalText: response
      };
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: generateId(),
      role: 'user',
      content: inputMessage.trim(),
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputMessage.trim();
    setInputMessage('');
    setIsLoading(true);

    try {
      // 调用 Coze API，使用销售日历专用的Bot ID
      const response = await cozeApiService.sendMessageWithBotId(currentInput, '7548423421655973907');
      
      if (response.success && response.message) {
        // 解析智能体的回复，提取结构化数据
        const parsedData = parseAssistantResponse(response.message);
        setDisplayData(parsedData);
        setDisplayType(parsedData.type as any);

        const assistantMessage: ChatMessage = {
          id: generateId(),
          role: 'assistant',
          content: response.message,
          timestamp: Date.now(),
          data: parsedData
        };
        
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        const errorMessage: ChatMessage = {
          id: generateId(),
          role: 'assistant',
          content: `抱歉，处理您的请求时出现错误：${response.error}`,
          timestamp: Date.now()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      const errorMessage: ChatMessage = {
        id: generateId(),
        role: 'assistant',
        content: '抱歉，系统出现错误，请稍后重试。',
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 渲染智能体回复内容
  const renderReportContent = () => {
    if (!displayData) {
      return (
        <div className="flex items-center justify-center h-full text-gray-500">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-calendar-line text-2xl text-gray-400"></i>
            </div>
            <p className="text-lg font-medium mb-2">销售日历面板</p>
            <p className="text-sm">与右侧AI助手对话，查看销售日历的智能分析</p>
          </div>
        </div>
      );
    }

    if (displayData.type === 'json') {
      return (
        <div className="p-6">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="border-b border-gray-200 pb-4 mb-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{displayData.title}</h1>
              <p className="text-gray-600">智能体结构化回复</p>
            </div>
            
            {/* 显示原始文本 */}
            {displayData.originalText && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">智能体回复</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{displayData.originalText}</p>
                </div>
              </div>
            )}

            {/* 显示结构化数据 */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">结构化数据</h3>
              <div className="bg-gray-900 rounded-lg p-4 overflow-auto">
                <pre className="text-green-400 text-sm font-mono">
                  {JSON.stringify(displayData.data, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (displayData.type === 'summary') {
      return (
        <div className="p-6 max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="border-b border-gray-200 pb-4 mb-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{displayData.title}</h1>
              <p className="text-gray-600">基于智能体分析生成</p>
            </div>

            {/* 智能体原始回复 */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <i className="ri-robot-line text-blue-600 mr-2"></i>
                智能体分析
              </h3>
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {displayData.data.analysis || displayData.data.originalResponse}
                </p>
              </div>
            </div>

            {/* 销售信息（如果有的话） */}
            {displayData.data.projectName && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">分析对象</h4>
                  <p className="text-gray-700">{displayData.data.projectName}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">分析时间</h4>
                  <p className="text-gray-700">{new Date().toLocaleString('zh-CN')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    } else {
      // 默认报告视图
      return (
        <div className="p-6 max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="border-b border-gray-200 pb-4 mb-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{displayData.title}</h1>
              <p className="text-gray-600">{displayData.data.period}</p>
            </div>

            {/* 智能体主要回复内容 */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-robot-line text-orange-600 mr-2"></i>
                  智能体回复
                </h3>
                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {displayData.data.summary}
                  </div>
                </div>
              </div>

              {/* 生成时间和来源信息 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-information-line text-blue-600 mr-2"></i>
                  生成信息
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">生成时间：</span>
                    <span className="font-medium">{new Date().toLocaleString('zh-CN')}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">数据来源：</span>
                    <span className="font-medium">Coze智能体</span>
                  </div>
                  <div>
                    <span className="text-gray-600">报告类型：</span>
                    <span className="font-medium">{displayData.title}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* 左侧回复展示区域 - 75% */}
      <div className="w-3/4 bg-white border-r border-gray-200 flex flex-col">
        {/* 头部信息栏 */}
        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-red-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                <i className="ri-calendar-line text-white text-lg"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{toolName}</h1>
                {functionName && (
                  <p className="text-sm text-gray-600">{functionName}</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="text-sm text-gray-600">
                <span className="font-medium">{userName}</span> - {userRole}
              </div>
              {displayData && (
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => setDisplayType('report')}
                    className={`p-2 rounded-lg transition-colors cursor-pointer ${
                      displayType === 'report' ? 'bg-orange-100 text-orange-600' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    title="报告视图"
                  >
                    <i className="ri-file-text-line"></i>
                  </button>
                  <button
                    onClick={() => setDisplayType('summary')}
                    className={`p-2 rounded-lg transition-colors cursor-pointer ${
                      displayType === 'summary' ? 'bg-orange-100 text-orange-600' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    title="摘要视图"
                  >
                    <i className="ri-list-check-line"></i>
                  </button>
                  <button
                    onClick={() => setDisplayType('json')}
                    className={`p-2 rounded-lg transition-colors cursor-pointer ${
                      displayType === 'json' ? 'bg-orange-100 text-orange-600' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    title="数据视图"
                  >
                    <i className="ri-code-line"></i>
                  </button>
                </div>
              )}
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-white/50 transition-colors cursor-pointer"
                title="返回工作台"
              >
                <i className="ri-home-line text-lg"></i>
              </button>
            </div>
          </div>
        </div>

        {/* 回复内容区域 */}
        <div className="flex-1 overflow-auto bg-gray-50">
          {renderReportContent()}
        </div>
      </div>

      {/* 右侧 AI 聊天区域 - 25% */}
      <div className="w-1/4 bg-white flex flex-col">
        {/* 聊天头部 */}
        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
              <i className="ri-robot-line text-white text-lg"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">销售日历智能体</h3>
              <p className="text-xs text-gray-500">ID: 7548423421655973907</p>
            </div>
          </div>
        </div>

        {/* 消息区域 */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] px-3 py-2 rounded-lg ${
                  message.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                <p className={`text-xs mt-1 ${
                  message.role === 'user' ? 'text-blue-100' : 'text-gray-500'
                }`}>
                  {formatTime(message.timestamp)}
                </p>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-lg px-3 py-2">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600"></div>
                  <span className="text-sm text-gray-600">智能体思考中...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* 输入区域 */}
        <div className="p-4 border-t border-gray-200">
          <div className="space-y-2">
            {/* 快捷按钮 */}
            <div className="flex space-x-1">
              <button
                onClick={() => setInputMessage('帮我安排本周的销售日程')}
                className="flex-1 px-2 py-1 text-xs bg-orange-50 text-orange-600 rounded hover:bg-orange-100 transition-colors cursor-pointer"
              >
                日程安排
              </button>
              <button
                onClick={() => setInputMessage('分析本月的销售业绩')}
                className="flex-1 px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors cursor-pointer"
              >
                业绩分析
              </button>
              <button
                onClick={() => setInputMessage('制定客户拜访计划')}
                className="flex-1 px-2 py-1 text-xs bg-purple-50 text-purple-600 rounded hover:bg-purple-100 transition-colors cursor-pointer"
              >
                客户拜访
              </button>
            </div>

            {/* 消息输入 */}
            <div className="flex space-x-2">
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="向销售日历助手描述您的需求..."
                disabled={isLoading}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-sm resize-none"
                rows={2}
              />
              <button
                onClick={handleSendMessage}
                disabled={isLoading || !inputMessage.trim()}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap self-end"
              >
                {isLoading ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  <i className="ri-send-plane-line"></i>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
